import { FC, useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { RadioButtonUncheckedOutlined, RadioButtonCheckedOutlined } from '@mui/icons-material';
import Search from '../Search';
import { TruncatingTypography } from '@components';

type FilterOption = { label: string; value: string };

interface TableFilterDropdownProps {
  selected: string[];
  setSelected: (filters: string[]) => void;
  options: FilterOption[];
  label?: string;
  showSingleSelectIcon?: boolean;
  showSelectAllAndReset?: boolean;
  showSearchInput?: boolean;
  menuMaxWidth?: number | string;
  /** Pinned option that cannot be combined with any other selection. Opt-in only. */
  exclusiveOption?: FilterOption;
}

const linkButtonSx = {
  padding: 0,
  minWidth: 'auto',
  fontSize: '12px',
  fontWeight: 700,
  color: '#2F736E',
  textTransform: 'none' as const,
  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
};

const optionLabelSx = {
  display: 'block',
  minWidth: 0,
  maxWidth: '100%',
};

const formControlLabelSx = {
  width: '100%',
  ml: 0,
  mr: 0,
  minWidth: 0,
  '& .MuiFormControlLabel-label': {
    minWidth: 0,
    overflow: 'hidden',
  },
};

const TableFilterDropdown: FC<TableFilterDropdownProps> = ({
  options,
  selected,
  setSelected,
  label = 'Filter',
  showSingleSelectIcon = false,
  showSelectAllAndReset = false,
  showSearchInput = false,
  menuMaxWidth = 300,
  exclusiveOption,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<FilterOption[]>([]);
  const exclusiveValue = exclusiveOption?.value;

  useEffect(() => {
    if (!options.length) return;
    setFilteredOptions(options);
  }, [options]);

  const handleColumnFilterSearch = (search: string) => {
    setFilteredOptions(
      options.filter(
        (opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase()) || selected.includes(opt.value),
      ),
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
    setFilteredOptions(options);
  };

  const handleSelectAll = () => {
    // Never include the exclusive option in "Select All".
    setSelected(options.map((opt) => opt.value));
    setSearch('');
  };

  const handleReset = () => {
    setSelected([]);
    setSearch('');
    setFilteredOptions(options);
  };

  const handleToggle = (option: string) => {
    if (exclusiveValue && option === exclusiveValue) {
      setSelected(selectedSet.has(exclusiveValue) ? [] : [exclusiveValue]);
      return;
    }

    const withoutExclusive = exclusiveValue
      ? selected.filter((value) => value !== exclusiveValue)
      : [...selected];

    if (selectedSet.has(option)) {
      setSelected(withoutExclusive.filter((value) => value !== option));
      return;
    }

    setSelected([...withoutExclusive, option]);
  };

  const renderOption = (
    optionLabel: string,
    value: string,
    { isRadioButton = false }: { isRadioButton?: boolean } = {},
  ) => (
    <MenuItem key={value} dense sx={{ maxWidth: '100%' }}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={selectedSet.has(value)}
            onChange={() => handleToggle(value)}
            {...((showSingleSelectIcon || isRadioButton) && {
              icon: <RadioButtonUncheckedOutlined />,
              checkedIcon: <RadioButtonCheckedOutlined />,
            })}
            inputProps={{ 'aria-label': `Filter by ${optionLabel}` }}
          />
        }
        label={
          <TruncatingTypography component="span" tooltipPlacement="top" sx={optionLabelSx}>
            {optionLabel}
          </TruncatingTypography>
        }
        sx={formControlLabelSx}
      />
    </MenuItem>
  );

  const isOpen = Boolean(anchorEl);
  const menuId = 'filter-menu';

  return (
    <>
      <Box
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {selected.length > 0 && (
          <Box
            sx={{
              width: '1.25rem',
              height: '1.25rem',
              bgcolor: '#FF6347',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#fff',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              {selected.length}
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={handleClick}
          aria-label={`${label} options`}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          color={selected.length > 0 ? 'primary' : 'default'}
        >
          <FilterAltOutlinedIcon
            sx={{
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#162C36',
            }}
          />
        </IconButton>
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'filter-button',
          sx: showSelectAllAndReset
            ? {
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 0,
              }
            : undefined,
        }}
        PaperProps={{
          sx: {
            maxHeight: '300px',
            maxWidth: menuMaxWidth,
            width: 'max-content',
            borderRadius: '0.5rem',
            ...(showSelectAllAndReset && {
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }),
            ...(!showSelectAllAndReset && { overflowY: 'auto' }),
          },
        }}
      >
        {showSelectAllAndReset && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            px="12px"
            py="8px"
            sx={{
              borderBottom: '1px solid #737F86',
              flexShrink: 0,
            }}
            gap={1.5}
          >
            <Typography fontSize="12px" fontWeight={700} color="#6C6C6C" marginRight="1.5rem">
              {label}
            </Typography>
            <Stack direction="row" gap={1.5}>
              <Button onClick={handleSelectAll} sx={linkButtonSx}>
                Select All
              </Button>
              <Button onClick={handleReset} sx={linkButtonSx}>
                Reset
              </Button>
            </Stack>
          </Stack>
        )}
        {showSearchInput && (
          <Search
            handleSearch={handleColumnFilterSearch}
            search={search}
            setSearch={setSearch}
            placeholder="Search"
            styles={{
              backgroundColor: '#fff',
            }}
          />
        )}
        {exclusiveOption && (
          <Box sx={{ flexShrink: 0 }}>
            {renderOption(exclusiveOption.label, exclusiveOption.value, { isRadioButton: true })}
            <Divider sx={{ borderColor: '#E8EAEB' }} />
          </Box>
        )}
        <Box sx={showSelectAllAndReset ? { overflowY: 'auto', flex: 1, minHeight: 0 } : undefined}>
          {filteredOptions.map(({ label: optionLabel, value }) => renderOption(optionLabel, value))}
        </Box>
      </Menu>
    </>
  );
};

export default TableFilterDropdown;
